import { useState, useMemo } from "react";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { ChemBackButton } from "./ChemBackButton.jsx";

const EM = "#F8B84E", EM_DEEP = "#7C3AED", EM_LIGHT = "#FCD9A0";
const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
};

/**
 * Count significant figures using standard rules:
 *  - All non-zero digits are significant.
 *  - Zeros between significant digits are significant.
 *  - Leading zeros are NOT significant.
 *  - Trailing zeros ARE significant only if there's a decimal point.
 *  - Scientific notation: only the mantissa digits count.
 */
function sigFigs(raw) {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;
  // scientific notation -> count mantissa sig figs
  const sci = s.match(/^[+-]?(\d+\.?\d*|\.\d+)[eE][+-]?\d+$/);
  if (sci) s = s.replace(/[eE][+-]?\d+$/, "");
  s = s.replace(/^[+-]/, "");
  if (!/^\d*\.?\d*$/.test(s) || s === "." || s === "") return null;

  const hasDot = s.includes(".");
  if (hasDot) {
    let digits = s.replace(".", "");
    // strip leading zeros (not significant)
    digits = digits.replace(/^0+/, "");
    if (digits === "") {
      // e.g. "0.000" — significant figures are the trailing zeros after
      // the decimal that are written; conventionally "0.00" has the zeros
      // as placeholders → treat as ambiguous, report the written zeros.
      const afterDot = s.split(".")[1] || "";
      return afterDot.length || 1;
    }
    return digits.length;
  } else {
    // integer, no decimal point: trailing zeros NOT significant
    let digits = s.replace(/^0+/, "");
    if (digits === "") return 1; // "0"
    const stripped = digits.replace(/0+$/, "");
    return stripped.length || 1;
  }
}

/** Round a number to n significant figures, returned as a string. */
function roundSig(numStr, n) {
  const x = parseFloat(numStr);
  if (!isFinite(x) || n < 1) return null;
  if (x === 0) return (0).toFixed(Math.max(0, n - 1));
  const d = Math.ceil(Math.log10(Math.abs(x)));
  const power = n - d;
  const factor = Math.pow(10, power);
  const rounded = Math.round(x * factor) / factor;
  // present with the right number of decimals where sensible
  if (power > 0) return rounded.toFixed(power);
  return String(rounded);
}

export default function SigFigs() {
  const [num, setNum] = useState("");
  const [roundTo, setRoundTo] = useState("3");

  const count = useMemo(() => sigFigs(num), [num]);
  const rounded = useMemo(() => {
    const n = parseInt(roundTo);
    if (num === "" || !isFinite(parseFloat(num)) || !n) return null;
    return roundSig(num, n);
  }, [num, roundTo]);

  const inStyle = { width: "100%", padding: "14px 16px", borderRadius: 10, border: `1px solid ${rgba(EM,0.22)}`, background: "rgba(0,0,0,0.3)", color: "#E2E8F0", fontSize: 17, fontFamily: "inherit", outline: "none", boxSizing: "border-box", textAlign: "center" };

  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", top: "-25%", left: "-10%", width: "50vw", height: "50vw", background: `radial-gradient(circle, ${rgba(EM,0.05)} 0%, transparent 65%)`, pointerEvents: "none" }} />
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", paddingTop: "calc(16px + env(safe-area-inset-top))", paddingLeft: "calc(24px + env(safe-area-inset-left))", paddingRight: "calc(24px + env(safe-area-inset-right))", borderBottom: `1px solid ${rgba(EM,0.06)}`, position: "relative", zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ChemBackButton accentColor={EM} />
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${EM}, ${EM_DEEP})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: "#fff" }}>Ch</div>
          <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", background: `linear-gradient(135deg, ${EM_LIGHT}, #A7F3D0)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Sig Figs</span>
          <span style={{ fontSize: 11, color: "#374151", marginLeft: 4 }}>by NerdChem</span>
        </div>
        <div style={{ fontSize: 12, color: "#374151" }}>100% free</div>
      </header>

      <main style={{ maxWidth: 480, margin: "0 auto", padding: "32px 20px 80px", position: "relative", zIndex: 10 }}>
        <p style={{ fontSize: 15, color: "#9CA3AF", lineHeight: 1.6, textAlign: "center", margin: "0 0 24px" }}>
          Count the significant figures in a number, and round any number to a
          chosen number of sig figs. Supports decimals and scientific notation.
        </p>

        <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Number</label>
        <input value={num} onChange={(e) => setNum(e.target.value)} placeholder="e.g. 0.004560 or 1.23e4" style={{ ...inStyle, marginTop: 6 }} />

        {count !== null && (
          <div style={{ marginTop: 22, textAlign: "center", padding: "22px", borderRadius: 16, background: rgba(EM,0.07), border: `1px solid ${rgba(EM,0.25)}` }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Significant figures</div>
            <div style={{ fontSize: 44, fontWeight: 800, color: EM_LIGHT, marginTop: 4 }}>{count}</div>
          </div>
        )}
        {num !== "" && count === null && (
          <div style={{ marginTop: 22, padding: "14px", borderRadius: 12, background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.25)", color: "#FDA4AF", fontSize: 14, textAlign: "center" }}>Not a valid number</div>
        )}

        <div style={{ marginTop: 30, paddingTop: 24, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Round to how many sig figs</label>
          <div style={{ display: "flex", gap: 7, marginTop: 8, flexWrap: "wrap" }}>
            {[1,2,3,4,5,6].map(n => (
              <button key={n} onClick={() => setRoundTo(String(n))} style={{ flex: "1 1 auto", minWidth: 44, padding: "10px", borderRadius: 9, cursor: "pointer", fontFamily: "inherit", fontSize: 15, fontWeight: 700, background: roundTo === String(n) ? rgba(EM,0.12) : "rgba(255,255,255,0.02)", border: roundTo === String(n) ? `1.5px solid ${rgba(EM,0.35)}` : "1.5px solid rgba(255,255,255,0.05)", color: roundTo === String(n) ? EM_LIGHT : "#6B7280" }}>{n}</button>
            ))}
          </div>
          {rounded !== null && (
            <div style={{ marginTop: 16, textAlign: "center", padding: "18px", borderRadius: 14, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Rounded to {roundTo} sig figs</div>
              <div style={{ fontSize: 30, fontWeight: 800, color: EM_LIGHT, marginTop: 4 }}>{rounded}</div>
            </div>
          )}
        </div>
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(EM,0.04)}` }}>
        <NerdStackFooter variant="tool" family="chem" />
      </footer>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap'); input::placeholder { color: #4B5563; } * { box-sizing: border-box; } body { margin: 0; }`}</style>
    </div>
  );
}
